import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { stepSpecialNeedsSchema, type StepSpecialNeedsForm } from './schemas';
import { useRegistrationStore } from '../../store/registrationStore';
import { Button } from '../Button';
import type { DietaryRestriction } from '../../types/registration';

const inputClasses =
  'w-full bg-slate-50 border border-slate-200 rounded-lg p-3 text-slate-700 placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none';
const labelClasses = 'block text-sm font-bold text-slate-700 mb-1.5';

const DIETARY_OPTIONS = [
  { value: 'vegetarian', label: 'Vegetarian' },
  { value: 'vegan', label: 'Vegan' },
  { value: 'gluten-free', label: 'Gluten-Free' },
  { value: 'halal', label: 'Halal' },
  { value: 'kosher', label: 'Kosher' },
  { value: 'dairy-free', label: 'Dairy-Free' },
  { value: 'nut-free', label: 'Nut-Free' },
];

interface StepSpecialNeedsProps {
  onNext: () => void;
  onBack: () => void;
}

export function StepSpecialNeeds({ onNext, onBack }: StepSpecialNeedsProps) {
  const { familyMembers, specialNeeds, setSpecialNeeds } = useRegistrationStore();

  const { register, handleSubmit } = useForm<StepSpecialNeedsForm>({
    resolver: zodResolver(stepSpecialNeedsSchema),
    defaultValues: {
      memberNeeds: familyMembers.map((_, i) => ({
        dietaryRestrictions: specialNeeds.memberNeeds[i]?.dietaryRestrictions || [],
        foodAllergies: specialNeeds.memberNeeds[i]?.foodAllergies || '',
        accessibilityNeeds: specialNeeds.memberNeeds[i]?.accessibilityNeeds || '',
      })),
    },
  });

  const onSubmitStep = (data: StepSpecialNeedsForm) => {
    setSpecialNeeds({
      memberNeeds: data.memberNeeds.map((m) => ({
        dietaryRestrictions: (m.dietaryRestrictions || []) as DietaryRestriction[],
        foodAllergies: m.foodAllergies || '',
        accessibilityNeeds: m.accessibilityNeeds || '',
      })),
    });
    onNext();
  };

  if (familyMembers.length === 0) {
    return (
      <div className="space-y-6">
        <h2 className="text-xl font-bold text-slate-900">Special Needs</h2>
        <p className="text-sm text-slate-600">
          No family members have been added yet. Please go back and add family members first.
        </p>
        <div className="flex justify-between">
          <Button type="button" variant="outline" onClick={onBack}>
            Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmitStep)} className="space-y-6">
      <h2 className="text-xl font-bold text-slate-900">Special Needs</h2>
      <p className="text-sm text-slate-600">
        Provide dietary restrictions, food allergies, and accessibility needs for each family member.
      </p>

      {familyMembers.map((member, index) => (
        <div key={index} className="border border-slate-200 rounded-lg p-4 space-y-4">
          <h3 className="text-sm font-bold text-slate-700">
            {member.firstName} {member.lastName}
          </h3>

          <div>
            <label className={labelClasses}>Dietary Restrictions</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {DIETARY_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    value={opt.value}
                    {...register(`memberNeeds.${index}.dietaryRestrictions`)}
                    className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor={`sn-allergy-${index}`} className={labelClasses}>
              Food Allergies
            </label>
            <input
              id={`sn-allergy-${index}`}
              className={inputClasses}
              placeholder="e.g., Peanuts - severe / EpiPen required"
              {...register(`memberNeeds.${index}.foodAllergies`)}
            />
          </div>

          <div>
            <label htmlFor={`sn-access-${index}`} className={labelClasses}>
              Accessibility / Mobility Requirements
            </label>
            <input
              id={`sn-access-${index}`}
              className={inputClasses}
              placeholder="e.g., Wheelchair accessible room needed"
              {...register(`memberNeeds.${index}.accessibilityNeeds`)}
            />
          </div>
        </div>
      ))}

      <div className="flex justify-between">
        <Button type="button" variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button type="submit" variant="primary">
          Next: Review
        </Button>
      </div>
    </form>
  );
}
